// Staff Web Push subscription helper for InsideChat notifications.
// Android-focused; iOS installed-PWA works too when permission is granted.
import axios from '@/axiosConfig';
import {
  persistPushEnabled,
  rememberPushEnabledAfterPwaUpdate,
  pushRestoreRetryDelaysMs,
} from './chatPushState';
import { isPwaUpdateCooldown } from './pwaUpdate';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function notificationPermission() {
  return pushSupported() ? Notification.permission : 'unsupported';
}

/** True when running as an installed PWA (required for Web Push on iOS). */
export function isStandalonePwa() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
    // iOS Safari legacy flag when launched from Home Screen
    if (window.navigator.standalone === true) return true;
  } catch (_) {
    /* ignore */
  }
  return false;
}

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function serviceWorkerUrl() {
  const base = process.env.BASE_URL || '/';
  const normalized = base.endsWith('/') ? base : `${base}/`;
  return `${normalized}service-worker.js`;
}

/**
 * Ensure we have a usable SW registration. Pixel/Chrome can hit "not ready"
 * when ready() hangs and no registration was found; re-register and wait for
 * an active worker when needed.
 */
async function getRegistration() {
  if (!('serviceWorker' in navigator)) return null;

  const swUrl = serviceWorkerUrl();
  let reg = null;

  try {
    reg = await navigator.serviceWorker.getRegistration();
  } catch (_) {
    /* ignore */
  }

  if (!reg) {
    try {
      reg = await navigator.serviceWorker.register(swUrl);
    } catch (e) {
      console.error('[push] service worker register failed:', e);
    }
  }

  // Do NOT SKIP_WAITING here. Activating a post-deploy waiting worker mid-session
  // races controllerchange reload with mixed precaches → blank grey PWA shell.
  // Push uses the already-active worker; users activate updates via Install dialog.

  // Wait only if there is no active worker yet (first install / cold register).
  if (reg && !reg.active) {
    const worker = reg.installing || reg.waiting;
    if (worker) {
      await new Promise((resolve) => {
        if (worker.state === 'activated') {
          resolve();
          return;
        }
        const t = setTimeout(resolve, 12000);
        worker.addEventListener('statechange', () => {
          if (worker.state === 'activated' || worker.state === 'redundant') {
            clearTimeout(t);
            resolve();
          }
        });
      });
    }
  }

  // Prefer ready (active controlling SW), with timeout
  try {
    const ready = navigator.serviceWorker.ready;
    const timeout = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('sw-ready-timeout')), 8000);
    });
    const readyReg = await Promise.race([ready, timeout]);
    if (readyReg) return readyReg;
  } catch (_) {
    /* fall through */
  }

  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    if (regs && regs.length) {
      return regs.find((r) => r.active) || regs[0];
    }
  } catch (_) {
    /* ignore */
  }

  return reg;
}

// Public VAPID key is not secret (private key stays server-only). Used when
// /api/push/vapid-key returns empty because production env failed to load keys.
const BUILTIN_VAPID_PUBLIC_KEY =
  'BDxWbpZ7LyInO8JDm77UNiFAlPg6KZMq1e-nH2N_ZiEEgPh2Yfh7xkEsx_kbabO10LvJhB-3gvHoRwtsVP7j1Wc';

/**
 * Public VAPID key from API, then env, then built-in fallback.
 */
async function fetchVapidPublicKey() {
  try {
    const { data } = await axios.get('/api/push/vapid-key', {
      params: { _: Date.now() },
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
      timeout: 12000,
    });
    if (data && data.publicKey) {
      return String(data.publicKey).trim();
    }
  } catch (e) {
    console.warn('[push] vapid-key API failed:', e && e.message);
  }

  const fromEnv = (process.env.VUE_APP_VAPID_PUBLIC_KEY || '').trim();
  if (fromEnv) return fromEnv;
  return BUILTIN_VAPID_PUBLIC_KEY;
}

/**
 * @returns {Promise<{ ok: boolean, reason: string, message: string, error?: string }>}
 */
async function doSubscribe() {
  if (!pushSupported()) {
    return {
      ok: false,
      reason: 'unsupported',
      message: 'Push notifications are not supported in this browser.',
    };
  }
  if (Notification.permission !== 'granted') {
    return {
      ok: false,
      reason: 'not_granted',
      message: 'Notification permission was not granted.',
    };
  }

  const reg = await getRegistration();
  if (!reg) {
    return {
      ok: false,
      reason: 'no_service_worker',
      message:
        'App service worker is not ready. Fully close the app, reopen it from the Home Screen, wait a few seconds, then try Enable Notifications again.',
    };
  }
  if (!reg.pushManager) {
    return {
      ok: false,
      reason: 'no_push_manager',
      message:
        'Push is not available on this device. On iPhone, install to Home Screen and use iOS 16.4+.',
    };
  }

  const publicKey = await fetchVapidPublicKey();
  if (!publicKey) {
    return {
      ok: false,
      reason: 'vapid_missing',
      message:
        'Push is not configured on the server (missing VAPID key). Contact an admin — server needs a restart with VAPID keys loaded.',
    };
  }

  let applicationServerKey;
  try {
    applicationServerKey = urlBase64ToUint8Array(publicKey);
  } catch (e) {
    return {
      ok: false,
      reason: 'vapid_invalid',
      message: 'Push configuration is invalid (bad VAPID key format).',
      error: e && e.message,
    };
  }

  let sub = null;
  try {
    sub = await reg.pushManager.getSubscription();
  } catch (e) {
    return {
      ok: false,
      reason: 'get_subscription_failed',
      message: 'Could not read existing push subscription.',
      error: e && e.message,
    };
  }

  // If an existing subscription was created with a different VAPID key,
  // resubscribe fails on iOS/Chrome — drop and recreate.
  if (sub) {
    try {
      const existingKey = sub.options && sub.options.applicationServerKey;
      const existing = existingKey ? new Uint8Array(existingKey) : null;
      const same =
        existing &&
        existing.length === applicationServerKey.length &&
        existing.every((v, i) => v === applicationServerKey[i]);
      if (!same) {
        await sub.unsubscribe();
        sub = null;
      }
    } catch (_) {
      try {
        await sub.unsubscribe();
      } catch (__) {
        /* ignore */
      }
      sub = null;
    }
  }

  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    } catch (e) {
      const msg = (e && e.message) || String(e);
      return {
        ok: false,
        reason: 'subscribe_failed',
        message: isIosDevice()
          ? `Could not create push subscription (${msg}). Open Pixie from the Home Screen icon (not Safari), ensure Notifications are allowed for Pixie in iOS Settings, then try again.`
          : `Could not create push subscription: ${msg}`,
        error: msg,
      };
    }
  }

  const json = sub.toJSON();
  if (!json || !json.endpoint || !json.keys) {
    return {
      ok: false,
      reason: 'invalid_subscription',
      message: 'Push subscription was incomplete. Try again.',
    };
  }

  try {
    await axios.post('/api/push/subscribe', {
      endpoint: json.endpoint,
      keys: json.keys,
    });
    persistPushEnabled(true);
    return {
      ok: true,
      reason: 'subscribed',
      message: 'Notifications enabled for chat updates.',
    };
  } catch (e) {
    const status = e && e.response && e.response.status;
    return {
      ok: false,
      reason: 'server_subscribe_failed',
      message:
        status === 401
          ? 'You must be signed in to enable notifications.'
          : 'Permission is granted, but the server could not save your subscription. Try again.',
      error: e && e.message,
    };
  }
}

const PUSH_RESTORE_NO_RETRY = new Set([
  'unsupported',
  'not_granted',
  'vapid_missing',
  'vapid_invalid',
]);

let restoreInFlight = null;

async function restorePushSubscription() {
  if (!pushSupported()) return false;
  if (Notification.permission !== 'granted') {
    persistPushEnabled(false);
    return false;
  }

  rememberPushEnabledAfterPwaUpdate({
    permission: Notification.permission,
    pwaUpdating: isPwaUpdateCooldown(),
  });

  const delays = pushRestoreRetryDelaysMs();
  for (let i = 0; i < delays.length; i++) {
    if (delays[i]) {
      await new Promise((resolve) => setTimeout(resolve, delays[i]));
    }
    const result = await doSubscribe();
    if (result.ok) return true;
    if (PUSH_RESTORE_NO_RETRY.has(result.reason)) return false;
  }
  return false;
}

// Silently attempt to (re)subscribe if permission is already granted.
// Needed after PWA Install/deploy: unregistering the SW drops the browser
// PushSubscription while Notification.permission stays "granted".
export async function subscribePushIfGranted() {
  if (restoreInFlight) return restoreInFlight;
  restoreInFlight = restorePushSubscription().finally(() => {
    restoreInFlight = null;
  });
  return restoreInFlight;
}

export function notifyPushRestored(ok) {
  if (typeof window === 'undefined' || !ok) return;
  try {
    window.dispatchEvent(
      new CustomEvent('pixie-push-restored', { detail: { ok: true } }),
    );
  } catch (_) {
    /* ignore */
  }
}

/**
 * Update the OS / PWA icon badge with total unread chat count.
 * Uses Badging API when available; no-ops on unsupported browsers.
 * @param {number} count
 */
export function setChatAppBadge(count) {
  if (typeof navigator === 'undefined') return;
  const n = Math.max(0, Math.floor(Number(count) || 0));
  try {
    if (n > 0 && typeof navigator.setAppBadge === 'function') {
      navigator.setAppBadge(n).catch(() => {});
    } else if (typeof navigator.clearAppBadge === 'function') {
      navigator.clearAppBadge().catch(() => {});
    }
  } catch (_) {
    /* ignore */
  }
}

/**
 * Prompts for permission (if needed) and subscribes. Must be called from a user gesture.
 * @returns {Promise<{ ok: boolean, permission: string, reason: string, message: string, error?: string }>}
 */
export async function enableNotifications() {
  if (!pushSupported()) {
    return {
      ok: false,
      permission: 'unsupported',
      reason: 'unsupported',
      message: 'Notifications are not supported on this device/browser.',
    };
  }

  // iOS only delivers Web Push for installed Home Screen PWAs
  if (isIosDevice() && !isStandalonePwa()) {
    return {
      ok: false,
      permission: Notification.permission,
      reason: 'not_standalone',
      message:
        'On iPhone, open Pixie from your Home Screen (Add to Home Screen), then enable notifications inside the app — not from Safari.',
    };
  }

  let perm = Notification.permission;
  if (perm === 'default') {
    try {
      perm = await Notification.requestPermission();
    } catch (e) {
      return {
        ok: false,
        permission: Notification.permission,
        reason: 'request_failed',
        message: 'Could not request notification permission.',
        error: e && e.message,
      };
    }
  }

  if (perm === 'denied') {
    persistPushEnabled(false);
    return {
      ok: false,
      permission: perm,
      reason: 'denied',
      message: isIosDevice()
        ? 'Notifications are blocked. On iPhone: Settings → Pixie (or Safari) → Notifications → Allow, then try again.'
        : 'Notifications are blocked. Enable them in browser/app settings, then try again.',
    };
  }

  if (perm !== 'granted') {
    return {
      ok: false,
      permission: perm,
      reason: 'not_granted',
      message: 'Notification permission was not granted.',
    };
  }

  const sub = await doSubscribe();
  return {
    ok: sub.ok,
    permission: perm,
    reason: sub.reason,
    message: sub.message,
    error: sub.error,
  };
}
